import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import useBlockData from "./useBlockData";
import FlexTable from "../../../components/ui-components/FlexTable";
import { fmtEur, fmtEurSigned } from "./format";

// TONE_CLASS mirrors HorizonTableBlock/PerformanceTableBlock/DriverTableBlock's mapping of
// the adverse/favorable/neutral vocabulary to a colour class: a group running over its
// budget (variance > 0) is adverse (red), one running under (variance < 0) is favorable (green).
const TONE_CLASS = {
  adverse: "a4-kpi-tone-adverse",
  favorable: "a4-kpi-tone-favorable",
  neutral: "a4-kpi-tone-neutral",
};

function varianceTone(variance) {
  if (variance > 0) return "adverse";
  if (variance < 0) return "favorable";
  return "neutral";
}

const columns = [
  { Header: "Group", accessor: "group" },
  { Header: "Cloud", accessor: "cloud", align: "right", Cell: ({ value }) => fmtEur(value) },
  { Header: "AI", accessor: "ai", align: "right", Cell: ({ value }) => fmtEur(value) },
  { Header: "Total", accessor: "total", align: "right", Cell: ({ value }) => fmtEur(value) },
  {
    Header: "Budget",
    accessor: "budget",
    align: "right",
    // budget === 0 means no per-group budget concept exists for this row (the backend sets it
    // to 0 rather than omitting it) - render "—" rather than "€0", which would misleadingly
    // read as a zero-dollar budget.
    Cell: ({ value }) => (value > 0 ? fmtEur(value) : "—"),
  },
  {
    Header: "Variance",
    accessor: "variance",
    align: "right",
    // A variance is only meaningful against a real budget: with budget === 0 the backend's
    // variance = total - 0 = total, which would render every row as adverse "over budget"
    // regardless of actual spend. Render neutral "—" instead.
    Cell: ({ value, row }) =>
      row.original.budget > 0 ? (
        <span className={TONE_CLASS[varianceTone(value)]}>{fmtEurSigned(value)}</span>
      ) : (
        <span className={TONE_CLASS.neutral}>—</span>
      ),
  },
];

/**
 * DetailedCostBlock - the appendix's detailed cost table, grouped by product/BU/provider
 * (detailed_cost block type).
 *
 * GET /api/v1/board-reports/{boardReportId}/runs/{runId}/figures/detailed-cost -> envelope
 * key "detailed_cost" -> boardfigures.DetailedCost { rows: [CostRow] } (internal/
 * boardfigures/quality.go's BuildDetailedCost): { group, cloud, ai, total, budget, variance },
 * Total = Cloud+AI and Variance = Total-Budget by construction, in the caller-supplied order
 * (boardFigureDetailedCostGroups sorts by descending Total; Budget is 0 for groups with no
 * per-group budget concept).
 */
const DetailedCostBlock = ({ title = "Detailed cost", boardReportId, runId }) => {
  const { data, isLoading, error } = useBlockData(
    `/api/v1/board-reports/${boardReportId}/runs/${runId}/figures/detailed-cost`
  );

  const rows = data?.detailed_cost?.rows ?? [];

  return (
    <BaseBlock title={title} isLoading={isLoading} error={error} className="a4-detailed-cost">
      {rows.length === 0 ? (
        <p className="a4-detailed-cost-empty">No detailed cost data available.</p>
      ) : (
        <FlexTable columns={columns} data={rows} getRowId={(row) => row.group} />
      )}
    </BaseBlock>
  );
};

DetailedCostBlock.propTypes = {
  title: PropTypes.string,
  boardReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DetailedCostBlock;
